import { isHotkey } from "is-hotkey";
import { keys } from "lodash";
import * as React from "react";
import { Editor, Element, Range, Transforms } from "slate";
import { ReactEditor, RenderElementProps, RenderLeafProps } from "slate-react";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { RichTextBlockElement } from "../types";

export type RichTextFormatType = "bold" | "italic" | "underline" | "code" | "strikethrough" | "break";
export type RichListFormatType = "numbered-list" | "bulleted-list";
export type RichBlockFormatType =
  | RichListFormatType
  | "list-item"
  | "paragraph"
  | "heading-one"
  | "heading-two"
  | "heading-three"
  | "heading-four"
  | "heading-five"
  | "heading-six"
  | "block-quote";
export type RichFormatType = RichTextFormatType | RichBlockFormatType;

export const TEXT_FORMATS: RichTextFormatType[] = ["bold", "italic", "underline", "code", "strikethrough"];
export const LIST_FORMATS: RichListFormatType[] = ["numbered-list", "bulleted-list"];
export const BLOCK_FORMATS: RichBlockFormatType[] = [...LIST_FORMATS, "heading-one", "heading-two", "block-quote"];
export const MARK_HOTKEYS: { [hotkey: string]: RichTextFormatType } = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
  "mod+shift+x": "strikethrough",
  "mod+shift+c": "code",
};

export const BLOCK_HOTKEYS: { [hotkey: string]: RichBlockFormatType } = {
  "mod+shift+7": "bulleted-list",
  "mod+shift+8": "numbered-list",
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isBlockActive(editor: Editor, format: RichBlockFormatType) {
  const match =
    Array.from(
      Editor.nodes(editor, {
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (node: any) => Element.isElement(node) && node.type === format,
      })
    ).length > 0;

  return match;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isMarkActive(editor: Editor, format: RichTextFormatType) {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
}

/* eslint-disable no-param-reassign */
export function withRichText(editor: Editor): Editor {
  const { normalizeNode } = editor;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  editor.deleteFragment = () => {
    const { selection } = editor;

    if (selection && Range.isExpanded(selection)) {
      Transforms.delete(editor, { hanging: true });
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  editor.normalizeNode = (entry: any) => {
    const [node, path] = entry;

    if (path.length === 0 && (node as RichTextBlockElement).children.length === 0) {
      Transforms.insertNodes(
        editor,
        { type: "paragraph", children: [{ text: "" }] },
        { match: Editor.isEditor, mode: "highest" }
      );

      if (ReactEditor.isFocused(editor)) {
        Transforms.select(editor, Editor.start(editor, []));
      }

      return;
    }

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const malformedNode: any = node;

    // We don't want to have nodes with type & text set.
    if (malformedNode.text !== undefined && malformedNode.type !== undefined) {
      Transforms.unsetNodes(editor, "type", { at: path });
      return;
    }

    if (
      malformedNode.type === undefined &&
      Array.isArray(malformedNode.children) &&
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      malformedNode.children.every((child: any) => Editor.isInline(editor, child))
    ) {
      Transforms.unwrapNodes(editor, { at: path });
      return;
    }

    normalizeNode(entry);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  editor.formatBlock = (format: any) => {
    const isActive = isBlockActive(editor, format);
    const isList = LIST_FORMATS.includes(format as RichListFormatType);

    if (BLOCK_FORMATS.includes(format)) {
      Editor.withoutNormalizing(editor, () => {
        LIST_FORMATS.forEach((f: RichBlockFormatType) => {
          Transforms.unwrapNodes(editor, {
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            match: (node: any) => Element.isElement(node) && node.type === f,
            split: true,
          });
        });

        const blockType = isList ? "list-item" : format;

        Transforms.setNodes(editor, {
          type: isActive ? "paragraph" : blockType,
        });

        if (!isActive && isList) {
          Transforms.wrapNodes(editor, { type: format, children: [] });
        }
      });
    }
  };

  return editor;
}
/* eslint-enable no-param-reassign */

export const ReactPart: import("./index").ReactPart = {
  renderElement(
    props: RenderElementProps,
    _editor: Editor,
    next: (props: RenderElementProps) => JSX.Element
  ): JSX.Element {
    const { attributes, element, children } = props;

    switch (element.type) {
      case "block-quote":
        return <blockquote {...attributes}>{children}</blockquote>;
      case "bulleted-list":
        return <ul {...attributes}>{children}</ul>;
      case "heading-one":
        return <h1 {...attributes}>{children}</h1>;
      case "heading-two":
        return <h2 {...attributes}>{children}</h2>;
      case "heading-three":
        return <h3 {...attributes}>{children}</h3>;
      case "heading-four":
        return <h4 {...attributes}>{children}</h4>;
      case "heading-five":
        return <h5 {...attributes}>{children}</h5>;
      case "heading-six":
        return <h6 {...attributes}>{children}</h6>;
      case "list-item":
        return <li {...attributes}>{children}</li>;
      case "numbered-list":
        return <ol {...attributes}>{children}</ol>;
      case "paragraph":
        return <p {...attributes}>{children}</p>;
      default:
        return next(props);
    }
  },
  renderLeaf(props: RenderLeafProps, _editor: Editor, next: (props: RenderLeafProps) => JSX.Element): JSX.Element {
    const { attributes, leaf } = props;
    let { children } = props;
    let match = false;

    if (leaf.placeholder) {
      return next(props);
    }

    if (leaf.bold) {
      children = <strong>{children}</strong>;
      match = true;
    }

    if (leaf.code) {
      children = <code>{children}</code>;
      match = true;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
      match = true;
    }

    if (leaf.underline) {
      children = <u>{children}</u>;
      match = true;
    }

    if (leaf.strikethrough) {
      children = <s>{children}</s>;
      match = true;
    }

    if (match) {
      return <span {...attributes}>{children}</span>;
    }
    return next(props);
  },
  onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    editor: Editor,
    next: (event: React.KeyboardEvent<HTMLDivElement>) => void
  ): void {
    keys(MARK_HOTKEYS).some((hotkey: string) => {
      if (isHotkey(hotkey, event.nativeEvent)) {
        const format = MARK_HOTKEYS[hotkey];
        const isActive = isMarkActive(editor, format);

        event.preventDefault();

        if (isActive) {
          editor.removeMark(format);
        } else {
          editor.addMark(format, true);
        }

        return true;
      }

      return false;
    });

    keys(BLOCK_HOTKEYS).some((hotkey: string) => {
      if (isHotkey(hotkey, event.nativeEvent)) {
        const format = BLOCK_HOTKEYS[hotkey];

        event.preventDefault();
        editor.formatBlock(format);

        return true;
      }

      return false;
    });

    next(event);
  },
};

const pluginSpec: import("./index").PluginSpec = [withRichText, ReactPart];
export default pluginSpec;
